import {
  QueryKey,
  useQuery,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { logger } from "@/monitoring/utils";
import api, { ApiCatalog, RestApiOptions } from "@/api";
import { User } from "@/types/models/User/v3/User.interface";
import ResponseError from "@/errors/ResponseError";

export const fetchUser = async (
  userId: string | null = "me",
  options?: RestApiOptions,
): Promise<User | null> => {
  try {
    const data: Omit<User, "is_authenticated"> = await api(options).get<
      Omit<User, "is_authenticated">
    >(ApiCatalog.user.endpoint, { urlReplacers: [{ id: userId }] });

    return { ...data, is_authenticated: true };
  } catch (err) {
    const responseError = err as Error;
    logger.error({ message: "Failed to fetch user", error: responseError });
    throw err;
  }
};

const useGetUser = (
  userId: string | undefined | null = "me",
  tanstackOptions?: Omit<
    UseQueryOptions<User | null, ResponseError, User | null, QueryKey>,
    "queryFn" | "queryKey"
  >,
) => {
  const query = useQuery<User | null, ResponseError>({
    retry: false,
    refetchOnMount: true,
    ...tanstackOptions,
    queryKey: [ApiCatalog.user.key, userId],
    queryFn: async () => await fetchUser(userId),
  });

  return { loading: query.isLoading, ...query };
};

export default useGetUser;
